import React from 'react';

/**
 * AiSummarySectionGroup Component
 * Renders a categorized section within the expanded AI summary modal (Spec §9, §10).
 * Sections: Requires Your Attention, Recent Progress, Important Changes, Conversations & Mentions, Other Relevant Updates.
 */
export default function AiSummarySectionGroup({
    sectionTitle,
    items,
    onItemActionClick
}) {
    if (!items || items.length === 0) return null;

    const getSectionMeta = (title) => {
        switch (title) {
            case 'Requires Your Attention':
                return {
                    dotColor: 'bg-amber-500',
                    borderClass: 'border-amber-200/80 dark:border-amber-900/40',
                    icon: 'warning',
                    iconColor: 'text-amber-600',
                };
            case 'Recent Progress':
                return {
                    dotColor: 'bg-emerald-500',
                    borderClass: 'border-emerald-200/80 dark:border-emerald-900/40',
                    icon: 'trending_up',
                    iconColor: 'text-emerald-600',
                };
            case 'Important Changes':
                return {
                    dotColor: 'bg-purple-500',
                    borderClass: 'border-purple-200/80 dark:border-purple-900/40',
                    icon: 'published_with_changes',
                    iconColor: 'text-purple-600',
                };
            case 'Conversations & Mentions':
                return {
                    dotColor: 'bg-blue-500',
                    borderClass: 'border-blue-200/80 dark:border-blue-900/40',
                    icon: 'alternate_email',
                    iconColor: 'text-blue-600',
                };
            case 'Other Relevant Updates':
            default:
                return {
                    dotColor: 'bg-slate-400',
                    borderClass: 'border-slate-200 dark:border-slate-800',
                    icon: 'info',
                    iconColor: 'text-slate-500',
                };
        }
    };

    const meta = getSectionMeta(sectionTitle);

    return (
        <div className="space-y-3">
            {/* Section Header */}
            <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-700/60 pb-1.5">
                <span className={`w-2 h-2 rounded-full ${meta.dotColor}`} />
                <span className={`material-symbols-outlined text-sm ${meta.iconColor}`}>
                    {meta.icon}
                </span>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300 font-headline">
                    {sectionTitle}
                </h3>
                <span className="text-[10px] font-bold text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded-full">
                    {items.length}
                </span>
            </div>

            {/* Section Items */}
            <div className="space-y-3">
                {items.map((item) => (
                    <div
                        key={item.id}
                        className={`bg-white dark:bg-slate-800/90 border ${meta.borderClass} rounded-xl p-4 shadow-2xs hover:shadow-xs transition-all space-y-2`}
                    >
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-sm text-[#03488A] dark:text-[#00AAF1]">
                                    {item.icon || 'folder'}
                                </span>
                                <span className="font-bold text-sm text-[#003263] dark:text-[#a7c8ff] font-headline">
                                    {item.projectName}
                                </span>
                            </div>
                            {item.entityType && (
                                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 dark:bg-slate-700/50 px-2 py-0.5 rounded border border-slate-200/50 dark:border-slate-700">
                                    {item.entityType}
                                </span>
                            )}
                        </div>

                        <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                            {item.detailedStory || item.text}
                        </p>

                        {/* Operational Deep-Link Action Button */}
                        {item.actionLabel && (
                            <div className="pt-2 flex justify-end">
                                <button
                                    onClick={() => onItemActionClick?.(item)}
                                    className="h-8 px-3.5 rounded-xl text-xs font-bold leading-none text-white bg-[#03488A] hover:bg-blue-800 transition-all flex items-center gap-1 shadow-xs cursor-pointer"
                                >
                                    <span>{item.actionLabel}</span>
                                    <span className="material-symbols-outlined text-xs">arrow_forward</span>
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
